import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function Profile() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('emba_access');
        const userData = localStorage.getItem('emba_user');

        if (!token) {
            navigate('/login');
            return;
        }

        if (userData) {
            try {
                const parsed = JSON.parse(userData);
                // Handle nested user object if present (from sign up response) or direct object (from login response if different)
                // Based on previous steps, sign up returns { user: { ... } } or { ... } depending on endpoint.
                // Login response example shows { user: { ... } }.
                // Let's handle both.
                setUser(parsed.user || parsed);
            } catch (e) {
                console.error("Failed to parse user data", e);
            }
        }
    }, [navigate]);

    const handleSignOut = () => {
        localStorage.clear();
        navigate('/login');
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-neutral-50 font-sans text-gray-900">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <Link to="/" className="text-xs tracking-[0.2em] font-bold uppercase hover:opacity-70 transition-opacity">
                        EMBA by Emefa
                    </Link>
                    <nav className="hidden md:flex gap-8 text-[10px] tracking-widest uppercase font-medium text-gray-500">
                        <Link to="/" className="hover:text-black transition-colors">Home</Link>
                        <Link to="/bookings" className="hover:text-black transition-colors">Bookings</Link>
                        <Link to="/my-bookings" className="hover:text-black transition-colors">My Appointments</Link>
                    </nav>
                    <button
                        onClick={handleSignOut}
                        className="text-[10px] uppercase tracking-widest text-red-500 hover:text-red-700 transition-colors border border-red-200 px-4 py-2 hover:bg-red-50"
                    >
                        Sign Out
                    </button>
                </div>
            </header>

            <main className="pt-32 pb-20 px-6 max-w-4xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1 className="text-3xl md:text-4xl font-serif mb-2">My Profile</h1>
                    <p className="text-sm text-gray-500 tracking-wide mb-10">Manage your personal information.</p>

                    <div className="bg-white p-8 md:p-12 shadow-sm border border-gray-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">

                            <div className="md:col-span-2 flex items-center gap-4 mb-4 pb-8 border-b border-gray-50">
                                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center text-xl font-serif">
                                    {user.first_name?.[0]}{user.last_name?.[0]}
                                </div>
                                <div>
                                    <h2 className="text-xl font-medium">{user.title} {user.first_name} {user.last_name}</h2>
                                    <p className="text-xs text-gray-400 tracking-widest uppercase">@{user.username}</p>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="block text-[10px] uppercase tracking-widest text-gray-400">Email Address</label>
                                <p className="text-sm font-medium">{user.email}</p>
                            </div>

                            <div className="space-y-1">
                                <label className="block text-[10px] uppercase tracking-widest text-gray-400">Phone Number</label>
                                <p className="text-sm font-medium">{user.phone_number}</p>
                            </div>

                            <div className="space-y-1">
                                <label className="block text-[10px] uppercase tracking-widest text-gray-400">Location</label>
                                <p className="text-sm font-medium">{user.city}, {user.country}</p>
                            </div>

                            <div className="space-y-1">
                                <label className="block text-[10px] uppercase tracking-widest text-gray-400">Gender</label>
                                <p className="text-sm font-medium">{user.gender === 'M' ? 'Male' : user.gender === 'F' ? 'Female' : user.gender}</p>
                            </div>

                            <div className="space-y-1">
                                <label className="block text-[10px] uppercase tracking-widest text-gray-400">Account Type</label>
                                <span className="inline-block bg-gray-100 px-3 py-1 text-[10px] tracking-wider uppercase font-medium text-gray-600">
                                    {user.role}
                                </span>
                            </div>

                        </div>
                    </div>

                    {/* Danger zone */}
                    <div className="mt-10 pt-8 border-t border-gray-100">
                        <h3 className="text-[10px] uppercase tracking-widest text-red-400 mb-4">Danger Zone</h3>
                        <div className="flex items-center justify-between flex-wrap gap-4 bg-red-50 border border-red-100 p-5">
                            <div>
                                <p className="text-sm font-medium text-gray-800">Delete Account</p>
                                <p className="text-xs text-gray-500 mt-0.5">Permanently remove your account and all associated data.</p>
                            </div>
                            <Link
                                to="/request-deletion"
                                className="shrink-0 border border-red-400 text-red-600 px-5 py-2 text-[10px] tracking-widest uppercase hover:bg-red-600 hover:text-white transition-colors"
                            >
                                Request Deletion
                            </Link>
                        </div>
                    </div>
                </motion.div>
            </main>
        </div>
    );
}
